import { useState, useEffect } from 'react';
import { X, Play, Eye, EyeOff, ChevronDown, ChevronRight, CheckCircle, AlertCircle, Clock, Zap } from 'lucide-react';
import { useManualExecution } from '../hooks/useExecutions';
import type { Flow, FormField } from '../types/api';

interface ExecutionLaunchModalProps {
  flow: Flow;
  isOpen: boolean;
  onClose: () => void;
  onExecutionStarted?: (executionId: string) => void;
}

interface WorkflowNode {
  id: string;
  type: string;
  label: string;
  position: { x: number; y: number };
  config?: any;
}

interface WorkflowEdge {
  source: string;
  target: string;
}

/**
 * Modal for launching workflow executions with manual trigger input
 */
export function ExecutionLaunchModal({ 
  flow, 
  isOpen, 
  onClose, 
  onExecutionStarted 
}: ExecutionLaunchModalProps) {
  const [inputData, setInputData] = useState<Record<string, any>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [triggeredBy, setTriggeredBy] = useState('manual');
  
  const manualExecutionMutation = useManualExecution();

  // Extract form fields from submission form
  const formFields = flow.submissionForm?.config?.fields || [];
  
  // Extract workflow nodes and edges
  const nodes: WorkflowNode[] = flow.dagDefinition?.nodes || [];
  const edges: WorkflowEdge[] = flow.dagDefinition?.edges || [];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setInputData({});
      setShowAdvanced(false);
      setExpandedNodes(new Set());
      setTriggeredBy('manual');
    }
  }, [isOpen]);

  // Initialize input data with default values from form fields
  useEffect(() => {
    if (formFields.length > 0) {
      const defaultData: Record<string, any> = {};
      formFields.forEach(field => {
        if (field.defaultValue !== undefined) {
          defaultData[field.name] = field.defaultValue;
        } else if (field.type === 'checkbox') {
          defaultData[field.name] = false;
        } else if (field.type === 'number') {
          defaultData[field.name] = 0;
        } else {
          defaultData[field.name] = '';
        }
      });
      setInputData(defaultData);
    }
  }, [formFields]);

  const handleInputChange = (fieldName: string, value: any) => {
    setInputData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const execution = await manualExecutionMutation.mutateAsync({
        flowId: flow.id,
        inputData,
        triggeredBy
      });
      
      onExecutionStarted?.(execution.id);
      onClose();
    } catch (error) {
      console.error('Failed to start execution:', error);
    }
  };

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getNodeIcon = (nodeType: string) => {
    switch (nodeType) {
      case 'email': return '📧';
      case 'api-call': return '🌐';
      case 'slack': return '💬';
      case 'condition': return '🔀';
      case 'transform': return '🔄';
      case 'wait': return '⏱️';
      case 'loop': return '🔄';
      case 'validator': return '✅';
      case 'database': return '🗄️';
      case 'hubspot': return '🎯';
      case 'webhook-caller': return '🔗';
      case 'webhook-listener': return '👂';
      case 'logger': return '📝';
      case 'approval': return '👤';
      case 'end': return '🏁';
      default: return '⚙️';
    }
  };

  const getNodeStatusColor = (nodeType: string) => {
    switch (nodeType) {
      case 'email':
      case 'slack':
      case 'webhook-caller':
        return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      case 'api-call':
      case 'database':
      case 'hubspot':
        return 'text-purple-600 bg-purple-50 dark:bg-purple-900/20';
      case 'condition':
      case 'transform':
      case 'validator':
        return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20';
      case 'wait':
      case 'loop':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'approval':
        return 'text-pink-600 bg-pink-50 dark:bg-pink-900/20';
      case 'end':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      default:
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Play className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Launch Execution
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {flow.name}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Input Data */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                  Trigger Data
                </h4>
                
                {/* Form Fields */}
                {formFields.length > 0 ? (
                  <div className="space-y-4">
                    {formFields.map(field => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        
                        {field.type === 'textarea' ? (
                          <textarea
                            value={inputData[field.name] || ''}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                          />
                        ) : field.type === 'select' ? (
                          <select
                            value={inputData[field.name] || ''}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select...</option>
                            {field.options?.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : field.type === 'checkbox' ? (
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={inputData[field.name] || false}
                              onChange={(e) => handleInputChange(field.name, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                              {field.label}
                            </span>
                          </div>
                        ) : (
                          <input
                            type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
                            value={inputData[field.name] || ''}
                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                            placeholder={field.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No form fields defined for this workflow</p>
                    <p className="text-sm">Execution will start with empty trigger data</p>
                  </div>
                )}

                {/* Advanced Options */}
                <div className="mt-6">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Advanced Options
                  </button>
                  
                  {showAdvanced && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Triggered By
                        </label>
                        <input
                          type="text"
                          value={triggeredBy}
                          onChange={(e) => setTriggeredBy(e.target.value)}
                          placeholder="manual"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Workflow Visualization */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                  Workflow Actions
                </h4>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {nodes.length > 0 ? (
                    <div className="space-y-2">
                      {nodes.map((node, index) => (
                        <div key={node.id} className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => toggleNodeExpansion(node.id)}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getNodeIcon(node.type)}</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {node.label || node.type}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getNodeStatusColor(node.type)}`}>
                                {node.type}
                              </span>
                            </div>
                            {expandedNodes.has(node.id) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          
                          {expandedNodes.has(node.id) && (
                            <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600">
                              <div className="pt-2 text-xs text-gray-600 dark:text-gray-400">
                                <div className="font-medium mb-1">Configuration:</div>
                                <pre className="bg-gray-100 dark:bg-gray-900 p-2 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(node.config || {}, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No workflow actions defined</p>
                    </div>
                  )}
                </div>

                {/* Execution Summary */}
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                  <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Execution Summary</span>
                  </div>
                  <div className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                    <div>• {nodes.length} action{nodes.length !== 1 ? 's' : ''} will be executed</div>
                    <div>• Trigger data: {Object.keys(inputData).length} field{Object.keys(inputData).length !== 1 ? 's' : ''}</div>
                    <div>• Estimated duration: {nodes.length * 2}-{nodes.length * 5} seconds</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {manualExecutionMutation.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    Starting execution...
                  </div>
                ) : (
                  'Ready to launch workflow execution'
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  disabled={manualExecutionMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleSubmit}
                  disabled={manualExecutionMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {manualExecutionMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Launch Execution
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 